import React, { useRef, useMemo, useState } from 'react';
import { Row, Col, Button, Spinner, Nav, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSync, faList, faChartLine, faChartBar, faBell, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useHistory, useLocation } from 'react-router-dom';
import Header from '../../../component/header/header';
import Sidebar from '../../../component/sidebar/sidebar';
import MeterCard from './components/MeterCard';
import FilterBar from './components/FilterBar';
import MeterForm from './components/MeterForm';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import MeterDashboardRouter from './routers/MeterDashboardRouter';
import AlertRulesList from './AlertRules/AlertRulesList';
import ReportRulesList from './ReportRules/ReportRulesList';
import { useMeters } from './hooks/useMeters';
import { useURLFilters } from './utils/urlHelpers';
import { useFinancialYears, useSources } from '../../../hooks/useApiData';
import './styles/MetersList.css';
import IotMeterAlerts from './IotMeterAlerts/IotMeterAlerts';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

const MetersList = () => {
  const history = useHistory();
  const location = useLocation();
  const { filters, updateFilters, clearFilters } = useURLFilters();
  const { data: meters = [], isLoading, error, refetch } = useMeters();
  const sources = useSources();
  const financialYears = useFinancialYears();
  const currentFinancialYear = financialYears.sort((a, b) => b.financial_year_value - a.financial_year_value)?.[0]?.financial_year_value;

  const meterFormRef = useRef(null);
  const deleteModalRef = useRef(null);

  // Get active tab from URL or default to 'list'
  const queryParams = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(queryParams.get('tab') || 'list');

  // Get companyId from user context or authentication
  // TODO: Replace with actual company ID retrieval logic
  // For example: const { user } = useAuth(); const companyId = user?.companyId;
  const currentUser = useCurrentUser();
  const companyId = currentUser.companyId;

  // Update URL when tab changes
  const handleTabChange = (key) => {
    setActiveTab(key);
    const params = new URLSearchParams(location.search);
    params.set('tab', key);
    history.push({ search: params.toString() });
  };

  // Convert sources to options format with sub-locations
  const sourceOptions = useMemo(() => 
    sources.map(s => ({
      value: s.id,
      label: s.unitCode || s.location?.area || 'Unknown',
      subLocations: (s.subLocation || []).map(sl => ({
        value: sl.id,
        label: sl.subLocation
      }))
    })), 
    [sources]
  );

  const financialYearOptions = useMemo(() => 
    financialYears.map(fy => ({
      value: fy.financial_year_value,
      label: fy.financial_year_value
    })), 
    [financialYears]
  );

  // Filter meters client-side
  const filteredMeters = useMemo(() => {
    let result = meters;

    if (filters.meterIds) {
      result = result.filter(m => 
        filters.meterIds.includes(m.meterId)
      );
    }
    if (filters.module) {
      result = result.filter(m => (m.financialYearKpis || []).find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi?.module === filters.module);
    }
    if (filters.category) {
      result = result.filter(m => (m.financialYearKpis || []).find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi?.category === filters.category);
    }
    if (filters.subCategory) {
      result = result.filter(m => (m.financialYearKpis || []).find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi?.subCategory === filters.subCategory);
    }
    if (filters.kpi) {
      result = result.filter(m => (m.financialYearKpis || []).find(fykpi => fykpi.financialYear === currentFinancialYear)?.kpi?.kpi === filters.kpi);
    }
    if (filters.sourceId) {
      result = result.filter(m => m.sourceId === filters.sourceId);
    }
    if (filters.subLocationId) {
      result = result.filter(m => m.subLocationId === filters.subLocationId);
    }

    return result;
  }, [meters, filters, currentFinancialYear]);

  const handleAddMeter = () => {
    meterFormRef.current?.show();
  };

  const handleEditMeter = (meter) => {
    meterFormRef.current?.show(meter);
  };

  const handleDeleteMeter = (meter) => {
    deleteModalRef.current?.show(meter);
  };

  const handleMeterClick = (meter) => {
    history.push(`/iot-meters/${meter.meterId}`);
  };

  const handleSave = () => {
    refetch();
  };

  const handleDelete = () => {
    refetch();
  };

  return (
    <div className="meters-list-page">
      <Row className="flex-nowrap m-0 p-0">
        <Col xs={2} className="sidebar-container p-0">
          <Sidebar activePage="iot-meters" />
        </Col>
        
        <Col xs={10} className="content-area p-0">
          <div style={{ position: 'sticky', top: 0, zIndex: 999 }}>
            <Header />
          </div>
          
          <div className="content-wrapper">
            <div className="page-header">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h1>IoT Meters</h1>
                  <p>Manage and monitor your IoT meters</p>
                </div>
                {activeTab === 'list' && (
                  <Button 
                    variant="primary" 
                    onClick={handleAddMeter}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Add Meter
                  </Button>
                )}
              </div>
            </div>

            {/* Tabs Navigation */}
            <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
              <Nav variant="tabs" className="meters-tabs mb-4">
                <Nav.Item>
                  <Nav.Link eventKey="list">
                    <FontAwesomeIcon icon={faList} className="me-2" />
                    Meters List
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="dashboard">
                    <FontAwesomeIcon icon={faChartLine} className="me-2" />
                    Dashboard
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="alert-rules">
                    <FontAwesomeIcon icon={faBell} className="me-2" />
                    Alert Rules
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="alerts">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    Alerts
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="report-rules">
                    <FontAwesomeIcon icon={faChartBar} className="me-2" />
                    Report Rules
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              <Tab.Content>
                {/* Meters List Tab */}
                <Tab.Pane eventKey="list">
                  {activeTab === 'list' && (
                    <>
                      <FilterBar
                        meters={meters}
                        filters={filters}
                        onFiltersChange={updateFilters}
                        onClearFilters={clearFilters}
                        financialYears={financialYearOptions}
                        sources={sourceOptions}
                      />

                      {error && (
                        <div className="alert alert-danger" role="alert">
                          Failed to load meters. Please try again.
                          <Button 
                            variant="outline-danger" 
                            size="sm" 
                            className="ms-3"
                            onClick={() => refetch()}
                          >
                            <FontAwesomeIcon icon={faSync} className="me-1" /> Retry
                          </Button>
                        </div>
                      )}

                      <div className="meters-container">
                        {isLoading ? (
                          <div className="text-center p-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Loading meters...</p>
                          </div>
                        ) : filteredMeters.length > 0 ? (
                          <>
                            <div className="mb-3 text-muted">
                              Showing {filteredMeters.length} of {meters.length} meters
                            </div>
                            <Row>
                              {filteredMeters.map(meter => (
                                <Col md={4} className="mb-4" key={meter.meterId}>
                                  <MeterCard
                                    meter={meter}
                                    onEdit={handleEditMeter}
                                    onDelete={handleDeleteMeter}
                                    onClick={handleMeterClick}
                                  />
                                </Col>
                              ))}
                            </Row>
                          </>
                        ) : (
                          <div className="text-center p-5">
                            <h4>No meters found</h4>
                            <p>
                              {meters.length === 0 
                                ? 'Get started by adding your first meter.'
                                : 'Try adjusting your filters to find meters.'
                              }
                            </p>
                            {meters.length === 0 ? (
                              <Button 
                                variant="primary" 
                                onClick={handleAddMeter}
                                className="mt-2"
                              >
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                Add Your First Meter
                              </Button>
                            ) : (
                              <Button 
                                variant="outline-primary" 
                                onClick={clearFilters}
                                className="mt-2"
                              >
                                <FontAwesomeIcon icon={faSync} className="me-2" />
                                Clear Filters
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </Tab.Pane>

                {/* Dashboard Tab - Uses Router */}
                <Tab.Pane eventKey="dashboard">
                  {activeTab === 'dashboard' && (
                    <MeterDashboardRouter
                      companyId={companyId}
                      meters={meters}
                      financialYearOptions={financialYearOptions}
                      sourceOptions={sourceOptions}
                      isLoading={isLoading}
                      onRefresh={refetch}
                    />
                  )}
                </Tab.Pane>

                {/* Alert Rules Tab */}
                <Tab.Pane eventKey="alert-rules">
                  {activeTab === 'alert-rules' && (
                    <AlertRulesList
                      meters={meters}
                      sources={sourceOptions}
                      currentFinancialYear={currentFinancialYear}
                    />
                  )}
                </Tab.Pane>

                {/* Iot Meter Alerts Tab */}
                <Tab.Pane eventKey="alerts">
                  {activeTab === 'alerts' && (
                    <IotMeterAlerts
                      meters={meters}
                      sources={sourceOptions}
                      currentFinancialYear={currentFinancialYear}
                      financialYearOptions={financialYearOptions}
                    />
                  )}
                </Tab.Pane>
                
                {/* Iot Meter Reports Tab */}
                <Tab.Pane eventKey="report-rules">
                  {activeTab === 'report-rules' && (
                    <ReportRulesList
                      meters={meters}
                      sources={sourceOptions}
                      currentFinancialYear={currentFinancialYear}
                      financialYearOptions={financialYearOptions}
                    />
                  )}
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </div>
        </Col>
      </Row>

      <MeterForm
        ref={meterFormRef}
        onSave={handleSave}
        sources={sourceOptions}
      />

      <DeleteConfirmModal
        ref={deleteModalRef}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default MetersList;